import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosApi from "../server/service";

import {
  Input,
  Button,
  ShadcnTable,
  ShadcnTableHeader,
  ShadcnTableBody,
  ShadcnTableRow,
  ShadcnTableHead,
  ShadcnTableCell,
  OrderActionMenu,
} from "../component/ui/TableComponent";

import { Plus, Search, Loader2 } from "lucide-react";

type OrderStatus =
  | "Not Started"
  | "Taka Arrived"
  | "Assignment to Master"
  | "Assignment to job-work"
  | "Assignment to Master/job-work"
  | "Assignment to Karigar"
  | "Done"
  | "Cancelled";

// const StatusBadge: React.FC<{ status: OrderStatus }> = ({ status }) => {
//   const statusInfo = {
//     "Not Started": {
//       text: "Not Started",
//       variant: "bg-slate-100 text-slate-800",
//     },
//     "Taka Arrived": {
//       text: "Taka Arrived",
//       variant: "bg-yellow-100 text-yellow-800",
//     },
//     "Assigned to Master": {
//       text: "Master Assigned",
//       variant: "bg-blue-100 text-blue-800",
//     },
//     "Assigned to Karigar": {
//       text: "Karigar Assigned",
//       variant: "bg-purple-100 text-purple-800",
//     },
//     Done: { text: "Done", variant: "bg-green-100 text-green-800" },
//     Cancelled: { text: "Cancelled", variant: "bg-red-100 text-red-800" },
//   };

//   const { text, variant } = statusInfo[status] || statusInfo["Not Started"];

//   return (
//     <span
//       className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variant}`}
//     >
//       {text}
//     </span>
//   );
// };

const StatusBadge: React.FC<{ status: OrderStatus | string }> = ({
  status,
}) => {
  // Accept string as fallback
  const statusInfo: Record<string, { text: string; variant: string }> = {
    "Not Started": {
      text: "Not Started",
      variant: "bg-slate-100 text-slate-800",
    },
    "Taka Arrived": {
      text: "Taka Arrived",
      variant: "bg-yellow-100 text-yellow-800",
    },
    "Assignment to Master": {
      text: "Master Assigned",
      variant: "bg-blue-100 text-blue-800",
    },
    "Assignment to job-work": {
      text: "Job Work Assigned",
      variant: "bg-cyan-100 text-cyan-800",
    }, // Added style
    "Assignment to Master/job-work": {
      text: "Master/Job Work Assigned",
      variant: "bg-teal-100 text-teal-800",
    }, // Added style
    "Assignment to Karigar": {
      text: "Karigar Assigned",
      variant: "bg-purple-100 text-purple-800",
    },
    Done: { text: "Done", variant: "bg-green-100 text-green-800" },
    Cancelled: { text: "Cancelled", variant: "bg-red-100 text-red-800" },
  };

  // Use the provided status or default to "Not Started" if unknown
  const { text, variant } = statusInfo[status] || {
    text: status,
    variant: "bg-gray-100 text-gray-800",
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variant}`}
    >
      {text}
    </span>
  );
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

// const WorkStatusDisplay: React.FC<{ status?: "completed" | "incomplete" }> = ({
//   status,
// }) => {
//   let text: string;
//   let colorClass: string;
//   let dotClass: string;

//   switch (status) {
//     case "completed":
//       text = "Work Completed";
//       colorClass = "text-green-700 bg-green-100";
//       dotClass = "bg-green-500";
//       break;

//     case "incomplete":
//       text = "Work Incomplete";
//       colorClass = "text-red-700 bg-red-100";
//       dotClass = "bg-red-500";
//       break;

//     default:
//       text = "In Progress";
//       colorClass = "text-amber-700 bg-amber-100";
//       dotClass = "bg-amber-500";
//       break;
//   }

//   return (
//     <div
//       className={`mt-1.5 inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${colorClass}`}
//     >
//       <span className={`h-2 w-2 rounded-full mr-1.5 ${dotClass}`}></span> {text}{" "}
//     </div>
//   );
// };

const WorkStatusDisplay: React.FC<{
  status?: "in progress" | "incomplete" | "completed";
}> = ({ status }) => {
  let text = "In Progress";
  let colorClass = "text-amber-700 bg-amber-100";
  let dotClass = "bg-amber-500";

  if (status === "completed") {
    text = "Work Completed";
    colorClass = "text-green-700 bg-green-100";
    dotClass = "bg-green-500";
  } else if (status === "incomplete") {
    text = "Work Incomplete";
    colorClass = "text-red-700 bg-red-100";
    dotClass = "bg-red-500";
  }

  return (
    <div
      className={`mt-1 inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${colorClass}`}
    >
      <span className={`h-2 w-2 rounded-full mr-1.5 ${dotClass}`} />
      {text}
    </div>
  );
};

interface OrderDetail {
  _id?: string;
  sizeSegmentId: {
    _id: string;
    segmentName: string;
  };
  quantity: number;
  totalPrice: number;
  unitPrice: number;

 masterAssignment?: {
    workStatus?: "in progress" | "incomplete" | "completed";
  };

  jobWorkAssignment?: {
    workStatus?: "in progress" | "incomplete" | "completed";
  };

  karigarAssignment?: {
    workStatus?: "in progress" | "incomplete" | "completed";
  };
}

interface OrderData {
  _id: string;
  lotNumber: string;
  // quantity: number;
  // totalPrice: number;
  status: OrderStatus;
  createdAt: string;
  designId: {
    _id: string;
    basicInfo: {
      designNo: string;
    };
  };

  // sizeSegmentId: {
  //   _id: string;
  //   segmentName: string;
  // };

  orderDetails: OrderDetail[];
  totalQuantity: number;
  grandTotalPrice: number;

  trackingData: {
    masterAssignment: {
      workStatus?: "completed" | "incomplete";
    };
    karigarAssignment: {
      workStatus?: "completed" | "incomplete";
    };
  };
}

const OrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10); // Items per page
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  console.log(searchInput, statusFilter, "params");
  const fetchOrders = useCallback(
    async (page: number) => {
      setLoading(true);
      try {
        // Making a real API call
        const response = await axiosApi.get("/api/orders/getAllOrders", {
          params: {
            search: searchInput,
            status: statusFilter,
            page: page,
            limit: pageSize,
          },
        });

        // Destructuring the data from the backend response
        // const { orders, totalPages, totalCount } = response.data.data;
        const orders = response.data.data || [];

        const totalCount = response.data.count || 0;

        setOrders(orders);
        setTotalOrderCount(totalCount);
        setTotalPages(Math.ceil(totalCount / pageSize));
      } catch (error) {
        console.error("Failed to fetch orders:", error);
        // You can add state here to show an error message to the user
      } finally {
        setLoading(false);
      }
    },
    [searchInput, statusFilter, pageSize]
  );

  // --- EFFECTS ---
  // Fetch when filters change (resets to page 1)
  useEffect(() => {
    setCurrentPage(1); // Go back to the first page on new search/filter
    fetchOrders(1);
  }, [searchInput, statusFilter]);

  // Fetch when page changes
  useEffect(() => {
    fetchOrders(currentPage);
  }, [currentPage, fetchOrders]);

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
            Order Management
          </h1>
          <p className="text-slate-600 mt-1">
            Manage production orders and track progress
          </p>
        </div>
        <Link to="/create-order" className="w-full md:w-auto">
          <Button className="w-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center gap-2 py-3">
            <Plus className="w-5 h-5" />
            <span className="font-medium">New Order</span>
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white shadow-md rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search orders..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full md:w-48 h-10 border border-slate-300 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            <option value="all">All Status</option>
            <option value="Not Started">Not Started</option>
            <option value="Taka Arrived">Taka Arrived</option>
            <option value="Assigned to Master">Assigned to Master</option>
            <option value="Assigned to Karigar">Assigned to Karigar</option>
            <option value="Done">Done</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Title */}
      <h2 className="text-xl font-semibold text-slate-700 mb-4">
        Production Orders ({totalOrderCount})
      </h2>

      {/* Data Display */}
      <div className="bg-white shadow-lg rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-12">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading Orders...</span>
          </div>
        ) : orders.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            No orders found.
          </div>
        ) : (
          <>
            <div className="hidden lg:block">
              <ShadcnTable>
                <ShadcnTableHeader>
                  <ShadcnTableRow>
                    <ShadcnTableHead>Design Name</ShadcnTableHead>
                    <ShadcnTableHead>Lot Number</ShadcnTableHead>
                    <ShadcnTableHead>Details</ShadcnTableHead>
                    <ShadcnTableHead>Total</ShadcnTableHead>
                    <ShadcnTableHead>Created Date</ShadcnTableHead>
                    <ShadcnTableHead>Status</ShadcnTableHead>
                    <ShadcnTableHead className="text-center">
                      Actions
                    </ShadcnTableHead>
                  </ShadcnTableRow>
                </ShadcnTableHeader>
                <ShadcnTableBody>
                  {orders.map((order) => (
                    <ShadcnTableRow key={order._id}>
                      <ShadcnTableCell className="font-medium">
                        {order.designId?.basicInfo?.designNo ?? "N/A"}
                      </ShadcnTableCell>
                      <ShadcnTableCell>{order.lotNumber}</ShadcnTableCell>

                      <ShadcnTableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-700">
                            {order.orderDetails && order.orderDetails.length > 0
                              ? order.orderDetails
                                  .map(
                                    (detail) =>
                                      detail.sizeSegmentId?.segmentName || "N/A"
                                  )
                                  .join(" | ")
                              : "N/A"}
                          </span>
                          <span className="text-xs text-slate-500 mt-1">
                            Total Qty: {order.totalQuantity || 0}
                          </span>
                        </div>
                      </ShadcnTableCell>
                      <ShadcnTableCell>
                        {formatCurrency(order.grandTotalPrice || 0)}
                      </ShadcnTableCell>
                      <ShadcnTableCell>
                        {new Date(order.createdAt).toLocaleDateString("en-IN")}
                      </ShadcnTableCell>



                      <ShadcnTableCell>
                        <div className="flex flex-col items-start gap-y-1.5">
                          <StatusBadge status={order.status as OrderStatus} />

                          {/* Show corresponding work status */}
                          {/* {order.status === "Assignment to Master" && (
                            <WorkStatusDisplay
                              status={
                                order.orderDetails[0]?.masterAssignment
                                  ?.workStatus
                              }
                            />
                          )}

                          {order.status === "Assignment to job-work" && (
                            <WorkStatusDisplay
                              status={
                                order.orderDetails[0]?.jobWorkAssignment
                                  ?.workStatus
                              }
                            />
                          )}

                          {order.status === "Assignment to Master/job-work" && (
                            <>
                              <WorkStatusDisplay
                                status={
                                  order.orderDetails[0]?.masterAssignment
                                    ?.workStatus
                                }
                              />
                              <WorkStatusDisplay
                                status={
                                  order.orderDetails[0]?.jobWorkAssignment
                                    ?.workStatus
                                }
                              />
                            </>
                          )}

                          {order.status === "Assignment to Karigar" && (
                            <WorkStatusDisplay
                              status={
                                order.orderDetails[0]?.karigarAssignment
                                  ?.workStatus
                              }
                            />
                          )} */}
                        </div>
                      </ShadcnTableCell>

                      <ShadcnTableCell className="flex justify-center items-center">
                        <OrderActionMenu orderId={order._id} />
                      </ShadcnTableCell>
                    </ShadcnTableRow>
                  ))}
                </ShadcnTableBody>
              </ShadcnTable>
            </div>

            {/* Mobile Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 lg:hidden">
              {orders.map((order) => (
                <div
                  key={order._id}
                  className="bg-white rounded-lg shadow-md border border-gray-100 p-4 space-y-3"
                >

                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-lg text-slate-800">
                      {order.designId?.basicInfo?.designNo ?? "N/A"}
                    </h3>
                    {/* This container will now stack the statuses */}
                    <div className="flex flex-col items-end">
                      <StatusBadge status={order.status as OrderStatus} />


                      {/* {order.status === "Assignment to Master" && (
                        <WorkStatusDisplay
                          status={
                            order.orderDetails[0]?.masterAssignment?.workStatus
                          }
                        />
                      )}

                      {order.status === "Assignment to job-work" && (
                        <WorkStatusDisplay
                          status={
                            order.orderDetails[0]?.jobWorkAssignment?.workStatus
                          }
                        />
                      )}

                      {order.status === "Assignment to Master/job-work" && (
                        <>
                          <WorkStatusDisplay
                            status={
                              order.orderDetails[0]?.masterAssignment
                                ?.workStatus
                            }
                          />
                          <WorkStatusDisplay
                            status={
                              order.orderDetails[0]?.jobWorkAssignment
                                ?.workStatus
                            }
                          />
                        </>
                      )}

                      {order.status === "Assignment to Karigar" && (
                        <WorkStatusDisplay
                          status={
                            order.orderDetails[0]?.karigarAssignment?.workStatus
                          }
                        />
                      )} */}
                    </div>
                  </div>

                  <div className="text-sm text-slate-600 space-y-1">
                    {/* <p>
                      {order.lotNumber} •{" "}
                      {`${order.sizeSegmentId.segmentName} • Qty: ${order.quantity}`}
                    </p>
                    <p>
                      <span className="font-medium">Total:</span>{" "}
                      {formatCurrency(order.totalPrice)}
                    </p> */}
                    <p>
                      {order.lotNumber} • Qty: {order.totalQuantity}
                    </p>
                    <p>
                      <span className="font-medium">Sizes:</span>{" "}
                      {order.orderDetails && order.orderDetails.length > 0
                        ? order.orderDetails
                            .map(
                              (detail) =>
                                detail.sizeSegmentId?.segmentName || "N/A"
                            )
                            .join(", ")
                        : "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Total:</span>{" "}
                      {formatCurrency(order.grandTotalPrice || 0)}
                    </p>

                    <p>
                      <span className="font-medium">Created:</span>{" "}
                      {new Date(order.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex justify-end border-t border-gray-300 pt-2">
                    <OrderActionMenu orderId={order._id} />
                  </div>
                </div>
              ))}
            </div>

            {/* PAGINATION CONTROLS */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between flex-wrap gap-4 p-4 border-t border-gray-300">
                <span className="text-sm text-slate-600">
                  Showing <span className="font-medium">{orders.length}</span>{" "}
                  of <span className="font-medium">{totalOrderCount}</span>{" "}
                  results
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage((p) => p - 1)}
                    disabled={currentPage === 1 || loading}
                  >
                    Previous
                  </Button>
                  <span className="text-sm font-medium px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={currentPage >= totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OrderManagement;
